import { DOCS_FORM_DEEP_LINK_PARAM } from "core/consts";
import { ClinicalConsultation, DocsFormName } from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import {
  DOCS_DEEP_LINK_VALIDATION_REASON,
  canOpenDeepLinkDischargeForm,
  getDocsDeepLinkErrorMessage,
  parseDischargeLetterDeepLinkForm,
} from "dsl/ecosystems/RecareDocs/deepLinkUtils";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslations } from "translations";

export function useDocsFormDeepLink({
  consultation,
  formModal,
  setFormModal,
}: {
  consultation: ClinicalConsultation | undefined;
  formModal: DocsFormName | null;
  setFormModal: (value: DocsFormName | null) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const translations = useTranslations();
  const hasOpenedDeepLinkFormRef = useRef(false);

  useEffect(() => {
    if (hasOpenedDeepLinkFormRef.current || formModal !== null) {
      return;
    }

    const formSlug = searchParams.get(DOCS_FORM_DEEP_LINK_PARAM);
    const parsedForm = parseDischargeLetterDeepLinkForm(formSlug);

    if (formSlug && !parsedForm) {
      const next = new URLSearchParams(searchParams);
      next.delete(DOCS_FORM_DEEP_LINK_PARAM);
      hasOpenedDeepLinkFormRef.current = true;
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
      toast({
        color: "danger",
        message: getDocsDeepLinkErrorMessage(
          translations,
          DOCS_DEEP_LINK_VALIDATION_REASON.INVALID_FORM,
        ),
      });
      return;
    }

    if (!parsedForm || !consultation) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete(DOCS_FORM_DEEP_LINK_PARAM);

    if (!canOpenDeepLinkDischargeForm({ consultation, formName: parsedForm })) {
      hasOpenedDeepLinkFormRef.current = true;
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
      toast({
        color: "danger",
        message: translations.general.error,
      });
      return;
    }

    hasOpenedDeepLinkFormRef.current = true;
    setFormModal(parsedForm);

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    consultation,
    formModal,
    searchParams,
    setFormModal,
    setSearchParams,
    toast,
    translations,
    translations.general.error,
  ]);
}
